import { alpha } from '@mui/material';
import { createTheme } from '@mui/material/styles';

import { AppThemeName, backgroundColorDarkTheme } from './global.styles';

const THEME_PALETTES: Record<
	AppThemeName,
	{
		bg: string;
		primary: string;
		primaryContrast: string;
		secondary: string;
		secondaryLight: string;
		infoContrast: string;
	}
> = {
	BLUE: {
		bg: '#0b1220',
		primary: '#1976d2',
		primaryContrast: '#ffffff',
		secondary: '#516f7f',
		secondaryLight: '#8c9ead',
		infoContrast: '#1a2035',
	},
	BLACK_WHITE: {
		bg: '#000000',
		primary: '#ffffff',
		primaryContrast: '#000000',
		secondary: '#555555',
		secondaryLight: '#b3b3b3',
		infoContrast: '#000000',
	},
};

export const buildTheme = (themeName: AppThemeName) => {
	const p = THEME_PALETTES[themeName] ?? THEME_PALETTES.BLUE;

	return createTheme({
		palette: {
			background: {
				default: p.bg,
				paper: '#ffffff',
			},
			text: {
				primary: '#ffffff',
				secondary: p.secondaryLight,
				disabled: alpha('#ffffff', 0.6),
			},
			primary: {
				main: p.primary,
				contrastText: p.primaryContrast,
			},
			secondary: {
				main: p.secondary,
				light: p.secondaryLight,
				contrastText: '#ffffff',
			},
			error: {
				main: '#c40000',
				contrastText: '#ffffff',
			},
			warning: {
				main: '#c48e19',
				contrastText: '#ffffff',
			},
			info: {
				main: '#ffffff',
				contrastText: p.infoContrast,
			},
			success: {
				main: '#149b36',
				contrastText: '#ffffff',
			},
		},
		components: {
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundColor: backgroundColorDarkTheme,
					},
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						'& .MuiInputBase-root': {
							'&:-webkit-autofill': {
								WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
								WebkitTextFillColor: '#000 !important',
								backgroundColor: 'transparent !important',
							},
						},
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						color: '#ffffff',
						'&.Mui-focused': {
							color: p.primary,
						},
						'&.Mui-disabled': {
							color: alpha('#ffffff', 0.6),
						},
					},
				},
			},
			MuiInput: {
				styleOverrides: {
					root: {
						color: '#ffffff',

						"&::before": {
							borderColor: p.secondary,
						},

						"&:hover:not(.Mui-disabled)::before": {
							borderColor: p.secondaryLight,
						},

						"&::after": {
							borderColor: p.primary,
						},

						"&.Mui-disabled": {
							color: alpha(p.secondary, 0.2),
							"&::before": {
								borderBottomStyle: "dotted",
								borderColor: alpha(p.secondary, 0.4),
							},
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: "6px",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: p.secondary,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: p.secondaryLight,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: p.primary,
						},
						"&.Mui-disabled": {
							backgroundColor: alpha(p.secondary, 0.2),
							"& .MuiOutlinedInput-notchedOutline": {
								borderColor: alpha(p.secondary, 0.4),
							},
						},
					},
				},
			},
			MuiList: {
				styleOverrides: {
					root: {
						padding: 0,
					},
				},
			},
			MuiListSubheader: {
				styleOverrides: {
					root: {
						backgroundColor: backgroundColorDarkTheme,
						color: p.secondaryLight,
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					groupLabel: {
						backgroundColor: backgroundColorDarkTheme,
						color: p.secondaryLight,
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						"&:hover": {
							backgroundColor: "#333",
						},
						"&.Mui-selected": {
							backgroundColor: "#444",
							"&:hover": {
								backgroundColor: "#555",
							},
						},
					},
				},
			},
		},
	});
};
